import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import auth, { setupAuth, setPublicKey, loadAppInfo } from '@src/features/auth';
import cart, { setupCart } from '@src/features/cart';
import product from '@src/features/product';
import order from '@src/features/order';
import sale from '@src/features/sale';
import i18n from '@src/features/i18nMessages';
import userPreferences, { setupLocale, setWidgetUrl } from '@src/features/userPreferences';
import globalParameters, { setGlobalParameter } from '@src/features/globalParameters';
import checkout from '@src/features/checkout';
import directLink from '@src/features/directLink';
import ui, { setScreenWidth } from '@src/features/ui';
import payment from '@src/features/payment';
import {
  AppsApi,
  BuyersApi,
  ProductsApi,
  CartsApi,
  OrdersApi,
  SalesApi,
  SaleItemsApi,
  FormInputsApi,
  PaymentOptionsApi,
  VouchersApi,
  SchedulingsApi,
  SeatMapsApi,
  DirectLinksApi,
  TransactionsApi,
  Configuration,
  ErrorResponse,
  ProductPackagesApi,
} from '@src/core/buyerApiClient';
import monitoringMiddleware from './monitoringMiddleware';
import { getStoredUtms, appendQueryParams, getSessionId } from '@src/common';

export const store = configureStore({
  reducer: {
    auth,
    cart,
    product,
    order,
    sale,
    globalParameters,
    userPreferences,
    i18n,
    checkout,
    directLink,
    payment,
    ui,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(monitoringMiddleware),
});

window.addEventListener('resize', () => {
  store.dispatch(setScreenWidth(window.innerWidth));
});

export type RootState = ReturnType<typeof store.getState>;
export type RootStore = EnhancedStore<RootState>;
export type AppDispatch = typeof store.dispatch;

declare const __BUYER_API_TARGET__: string;

export const getClient = (state: RootState) => {
  const cfg = new Configuration({
    basePath: __BUYER_API_TARGET__.replace(/\/$/, ''),
    apiKey: state.auth.publicApiKey,
    baseOptions: {
      headers: {
        'X-Client-Language': state.userPreferences.originalLocale,
        'Cache-Control': 'no-store',
      },
      timeout: 45000,
    },
  });

  return {
    apps: new AppsApi(cfg),
    buyers: new BuyersApi(cfg),
    products: new ProductsApi(cfg),
    carts: new CartsApi(cfg),
    orders: new OrdersApi(cfg),
    sales: new SalesApi(cfg),
    saleItems: new SaleItemsApi(cfg),
    formInputs: new FormInputsApi(cfg),
    paymentOptions: new PaymentOptionsApi(cfg),
    vouchers: new VouchersApi(cfg),
    schedulings: new SchedulingsApi(cfg),
    seatMaps: new SeatMapsApi(cfg),
    directLinks: new DirectLinksApi(cfg),
    transactions: new TransactionsApi(cfg),
    productPackage: new ProductPackagesApi(cfg),
  };
};

export interface PayloadMap<T> {
  id: string;
  value: T;
}

export const bearerAuth = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export type ValidationError = {
  [key: string]: string[];
};

export const validationErrors = (err: unknown) => {
  if ((err as AxiosError).response?.status == 422) {
    const errorResponse = (err as AxiosError).response?.data as ErrorResponse;

    return errorResponse.errors?.reduce<ValidationError>(
      (carry, err) => ({
        ...carry,
        [err.path]: [...(carry[err.path] ?? []), getErrorMessage(err.type)],
      }),
      {},
    );
  }

  return undefined;
};

// TODO add translation in the future
export const getErrorMessage = (errType: string) => {
  switch (errType) {
    case 'required':
      return 'O campo é obrigatório';
    case 'invalidValue':
      return 'Valor inválido';
    case 'invalidOption':
      return 'Opção inválida';
    case 'unique':
      return 'Valor já está em uso';
    case 'emailMustBeUnique':
      return 'Email já está em uso';
    case 'emailDomainMustBeValid':
      return 'Email inválido';
    case 'phoneMustBeValid':
      return 'Telefone inválido';
    case 'passwordMustBeEqual':
      return 'As senhas não coincidem';
    case 'min':
      return 'Mínimo de 8 caracteres';
    default:
      return errType;
  }
};

export const validationErrorsWithoutI18n = (err: unknown) => {
  if ((err as AxiosError).response?.status == 422) {
    const errorResponse = (err as AxiosError).response?.data as ErrorResponse;

    return errorResponse.errors?.reduce<ValidationError>(
      (carry, err) => ({
        ...carry,
        [err.path]: [...(carry[err.path] ?? []), err.type],
      }),
      {},
    );
  }

  return undefined;
};

export const initializeStore = (key: string, language: string, url: string) => async (dispatch: AppDispatch) => {
  dispatch(setGlobalParameter('sessionId', getSessionId()));
  dispatch(setWidgetUrl(url));
  dispatch(setupLocale(language));
  dispatch(setPublicKey(key));
  setTimeout(() => {
    dispatch(loadAppInfo());
  }, 200);
  dispatch(setupAuth(false));
  dispatch(setupCart());
};

export const changeLocation = (url: string) => () => {
  const storedUtms = getStoredUtms();

  window.location.href = appendQueryParams(url, storedUtms) ?? url;
};

export const sendFbPixelEvent =
  (pixelId: string, accessTokenPath: string, eventData: any) => async (_: AppDispatch, getState: () => RootState) => {
    const {
      auth: { publicApiKey },
    } = getState();
    const url = __BUYER_API_TARGET__.replace(/\/$/, '') + '/integrations/facebookPixel/events';

    if (publicApiKey) {
      await axios.post(
        url,
        { pixelId, accessTokenPath, eventData },
        {
          headers: { 'X-App-Token': publicApiKey },
        },
      );
    }
  };
